import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CAP_MAX_USD, CAP_MIN_USD, TOP_UPS, type Affordability } from "@joogoos/contracts";
import { api, page } from "../api.js";

/**
 * The three things every screen that talks about money needs: buying some, raising a ceiling, and the one dialog
 * that appears when a ceiling is in the way
 * ([ADR 0012](../../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md)).
 *
 * Here rather than in the Credits panel because the Studio needs all three too, and at the exact moment somebody is
 * stopped. ADR 0012's whole argument is that a ceiling has to be raisable **where you hit it**, in one tap: a
 * ceiling that sends you to another screen to raise is a ceiling people set too high out of fear, and then it
 * protects nobody.
 */

/** Asked for wherever money is spoken about, so two screens cannot disagree about the balance. */
export const useCredits = () => useQuery({ queryKey: ["credits"], queryFn: api.credits });

/** Both screens that read a cap have to be told when one moves: the plan reads the month off `/v1/workspace`. */
/** What a post and a generated video cost at the margin the owner set, the site's own figures (`site/src/data/prices.ts`). */
const POST_USD = 0.2118;
const VIDEO_USD = 1.56852;

export function useMoneyMoved() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["credits"] });
    void qc.invalidateQueries({ queryKey: ["workspace"] });
  };
}

/**
 * Buy credit. Pressing a pack leaves this app for the card processor's own page.
 *
 * **No card number is ever typed into a Joogoos page** — the answer is a URL on the processor's domain and the
 * browser goes there — and that is worth saying on screen, because somebody about to type a card number has every
 * right to wonder where it is going.
 *
 * On a box with no card keys the packs draw and are disabled with a word about why, rather than failing when pressed
 * ([ADR 0028](../../../../docs/adr/0028-the-product-runs-on-real-providers-only-and-fakes-are-test-doubles.md)).
 */
export function TopUpPacks({ packs, connected, small }: { packs?: number[] | undefined; connected: boolean; small?: boolean }) {
  const offered = packs?.length ? packs : [...TOP_UPS];
  const start = useMutation({
    mutationFn: api.topUp,
    // Not `window.open`: a pop-up blocker eats it, and paying is not a side errand. The tab comes back to
    // `/?topup=done`, which opens Credits again (`App.tsx`).
    onSuccess: (r) => page.go(r.url),
  });

  return (
    <div>
      <div className="grid grid-cols-3 gap-2.5">
        {offered.map((p) => (
          <button
            key={p}
            onClick={() => start.mutate(p)}
            disabled={!connected || start.isPending}
            title={connected ? `Pay $${p} by card` : "Card payments are not connected yet"}
            className={`group bg-panel border border-line rounded-[10px] text-left flex flex-col enabled:hover:border-coral enabled:hover:bg-tint enabled:hover:shadow-[0_0_0_2px_var(--color-tint)] disabled:opacity-60 ${
              small ? "px-2.5 py-2 gap-0.5" : "px-3.5 py-3 gap-1.5"
            }`}
          >
            <span className="flex items-baseline justify-between gap-2">
              <span className={`font-display font-bold tabular-nums leading-none ${small ? "text-[17px]" : "text-[24px]"}`}>${p}</span>
              {/* The verb, because a number in a box is a fact until something says it can be pressed. */}
              {!small && connected ? (
                <span className="text-[11px] font-semibold text-faint group-hover:text-coral">Buy</span>
              ) : null}
            </span>
            <span className={`text-muted leading-snug ${small ? "text-[11px]" : "text-[11.5px]"}`}>
              about {Math.floor(p / VIDEO_USD)} videos, or {Math.floor(p / POST_USD)} posts
            </span>
          </button>
        ))}
      </div>
      {start.isPending ? <p className="text-[12px] text-muted mt-1.5">Opening the payment page…</p> : null}
      {start.error ? <p role="alert" className="text-[12px] text-bad mt-1.5">{String(start.error)}</p> : null}
      {!connected ? (
        <p className="text-[12px] text-muted mt-1.5">Card payments are not connected on this server, so these cannot be pressed.</p>
      ) : null}
    </div>
  );
}

/**
 * Raise one of the person's own ceilings, in a field and a press.
 *
 * The number is bounded by the API and by the same two constants the API reads, so the field can say what it will
 * accept before somebody types something it will refuse.
 */
export function RaiseLimit({ which, currentUsd, suggestUsd, onDone }: {
  which: "daily" | "monthly";
  currentUsd: string | null;
  /** What to put in the box: enough to cover what was just refused, rounded up to something readable. */
  suggestUsd?: string | undefined;
  onDone?: (() => void) | undefined;
}) {
  const moved = useMoneyMoved();
  const [usd, setUsd] = useState(suggestUsd ?? (currentUsd === null ? "" : Number(currentUsd).toFixed(2)));
  const save = useMutation({
    mutationFn: (value: string) => api.setLimits(which === "daily" ? { dailyUsd: value } : { monthlyUsd: value }),
    onSuccess: () => { moved(); onDone?.(); },
  });

  return (
    <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(usd); }}>
      <span className="text-[13px] text-muted">$</span>
      <input
        autoFocus
        inputMode="decimal"
        aria-label={which === "daily" ? "Daily limit in dollars" : "Monthly limit in dollars"}
        value={usd}
        onChange={(e) => setUsd(e.target.value)}
        className="w-24 px-2 py-1 rounded-lg border border-line-2 bg-page text-[13px] tabular-nums"
      />
      <span className="text-[11.5px] text-muted">${CAP_MIN_USD}–${CAP_MAX_USD}</span>
      <button type="submit" disabled={save.isPending} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-medium disabled:opacity-60">
        {save.isPending ? "Saving…" : `Raise the ${which === "daily" ? "daily" : "monthly"} limit`}
      </button>
      {onDone ? <button type="button" onClick={onDone} className="px-2 py-1.5 text-xs text-muted">Cancel</button> : null}
      {save.error ? <p role="alert" className="text-[12px] text-bad basis-full">{String(save.error)}</p> : null}
    </form>
  );
}

/**
 * The dialog with two doors, and neither of them is a dead end (ADR 0012 point 3).
 *
 * It is not an error, and it deliberately does not look like one. Two of the three reasons are the person's own
 * ceiling doing exactly what they set it to do; the third is an empty balance, which no ceiling can explain away and
 * which is the only one whose answer is money.
 *
 * **Carry on by hand** is the second door and it is not a consolation prize (ADR 0012 point 4): trimming,
 * reordering, writing your own words, swapping music, choosing between versions already made and *playing the whole
 * thing in the browser* all go on working. What waits is anything that asks a provider for something new.
 */
export function AtYourLimit({ why, onCarryOn }: { why: Exclude<Affordability, { ok: true }>; onCarryOn?: (() => void) | undefined }) {
  const credits = useCredits();
  const [raising, setRaising] = useState(false);
  const limits = credits.data?.limits;

  // Never having bought anything and having spent it all are the same number and not the same sentence. Since there
  // is no trial (ADR 0048), the first one is what a new customer meets, and "of credit left" would be telling them
  // they had lost something they never had.
  const firstTime = credits.data ? credits.data.lots.total === 0 : false;
  const said = why.reason === "credit"
    ? firstTime
      ? { title: "Add credit to start", body: "Joogoos is paid for as it is used: credit is dollars, spent per call and itemised, with no subscription and nothing taken monthly. Add some and press Generate — what you have set up here is waiting." }
      : { title: `$${why.leftUsd} of credit left, and this needs more`, body: "Credit is dollars and it is yours: every workspace you have spends from this one balance. Nothing here has been lost — top up and press Generate again." }
    : why.reason === "monthly_cap"
      ? { title: `$${why.leftUsd} left of this month's limit`, body: "You set this ceiling, and it is doing what you set it to. Raise it and this goes through; or leave it, and everything that costs nothing still works — trim, reorder, write your own words, and play the whole thing in the browser." }
      : { title: `$${why.leftUsd} left of today's limit`, body: "Your daily limit across all your workspaces. Raise it and this goes through, or come back tomorrow — nothing waiting is lost, and everything that costs nothing still works." };

  return (
    <div className="bg-tint border border-coral/30 rounded-[10px] px-4 py-3.5 flex flex-col gap-2.5" role="group" aria-label="At your limit">
      <div>
        <div className="text-[13.5px] font-semibold">{said.title}</div>
        <p className="text-[12.5px] text-ink-2 leading-relaxed max-w-[58ch] mt-0.5">{said.body}</p>
      </div>

      {why.reason === "credit" ? (
        <TopUpPacks packs={credits.data?.packs} connected={credits.data?.cardPayments ?? false} small />
      ) : raising ? (
        <RaiseLimit
          which={why.reason === "monthly_cap" ? "monthly" : "daily"}
          currentUsd={(why.reason === "monthly_cap" ? limits?.monthlyCapUsd : limits?.dailyCapUsd) ?? null}
          onDone={() => setRaising(false)}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setRaising(true)} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold">
            Raise the limit
          </button>
          {onCarryOn ? (
            <button onClick={onCarryOn} className="px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page">
              Carry on by hand
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
